import axios, { type AxiosResponse, type AxiosError } from 'axios';
import { getApiBaseUrl } from './apiConfig';
import type { GuestConvertSummary } from '../utils/guestConvert';
import { resolveOAuthRedirect } from '../utils/oauthRedirect';
import { getPasskey } from '../utils/webauthnBrowser';

// Interfaces
export interface AuthUser {
  id: string;
  email?: string;
  username: string;
  firstName?: string;
  lastName?: string;
  role: string;
  accountType?: string;
  [key: string]: any;
}

export interface AuthResponseData {
  token: string;
  user: AuthUser;
  message?: string;
}

/**
 * A guest conversion additionally reports what it carried across, so the caller
 * can confirm it to the fan. Absent on the legacy in-place path (which moves
 * nothing) and on any older server, so treat it as optional at every use.
 */
export type ConversionResponseData = AuthResponseData & { claimed?: GuestConvertSummary };

/** Which second factor an account is enrolled in. */
export type TwoFactorMethod = 'totp' | 'sms';

/**
 * Outcome of a login attempt.
 *
 * A discriminated union rather than a bare `AuthResponseData` because a
 * 2FA-enrolled account returns **no user** — the server withholds the session
 * until the second factor lands. The old signature made that state
 * indistinguishable from success: `login()` checked `if (response.data.user)`
 * and otherwise resolved silently with nobody signed in, so every caller
 * reported "logged in" and then sat there. Anyone enabling 2FA was locked out
 * of the UI. Callers must now handle `status`.
 */
export type LoginResult =
  | { status: 'ok'; data: AuthResponseData }
  | {
      status: '2fa';
      /** Short-lived proof that the password step passed. Not a session. */
      tempToken: string;
      /** `'sms'` means call `requestTwoFactorCode` and wait for a text. */
      method: TwoFactorMethod;
    };

// Token storage keys
//
// TOKEN_KEY ('auth_token') is no longer the source of truth — the JWT
// lives in the HttpOnly `showliner_session` cookie set by the backend
// at login. We keep the constant only to clean it out of localStorage
// for upgrading users (see clearAuthData and the boot-time wipe). PR3
// will remove it entirely.
const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';
const GUEST_ID_KEY = 'guest_id';

/**
 * Authentication service for handling JWT auth.
 *
 * Auth vehicle: HttpOnly `showliner_session` cookie on `.showliner.io`
 * (set by the backend at login, read automatically by every axios
 * request because apiClient sets `axios.defaults.withCredentials`).
 *
 * Bearer-header / localStorage-token methods (`getToken`, `setToken`,
 * `getAuthHeader`) are NEUTERED in PR2 — they remain on the class so
 * existing callsites compile, but they're no-ops or return empty.
 * PR3 removes them entirely.
 *
 * `getUser`/`setUser` keep using `auth_user` localStorage as a
 * presentation cache (avoids a flash-of-unauthenticated-content on
 * page load); the cookie is still the actual auth.
 */
class AuthService {
  // Same-tab subscribers notified whenever the cached user changes. The
  // browser `storage` event only fires in OTHER tabs, so writes made in this
  // tab (e.g. a guest identity established via the socket `joinSuccess` flow)
  // would otherwise go unnoticed by React state until a remount. UserContext
  // subscribes here so the in-memory `user` tracks `setUser`/`clearAuthData`
  // immediately. See the guest-banner-on-first-join fix.
  private userChangeListeners = new Set<(user: AuthUser | null) => void>();

  /**
   * Subscribe to cached-user changes. Returns an unsubscribe function.
   */
  onUserChanged(listener: (user: AuthUser | null) => void): () => void {
    this.userChangeListeners.add(listener);
    return () => {
      this.userChangeListeners.delete(listener);
    };
  }

  private notifyUserChanged(): void {
    const current = this.getUser();
    for (const listener of this.userChangeListeners) {
      try {
        listener(current);
      } catch (err) {
        console.error('authService user-change listener failed:', err);
      }
    }
  }

  private get authUrl(): string {
    return `${getApiBaseUrl()}/api/auth/`;
  }

  private get usersUrl(): string {
    return `${getApiBaseUrl()}/api/users/`;
  }

  /**
   * Make an account. `password` may be empty when `code` is the emailed code
   * from `requestOTP(email, 'signup')`: the code proves the address, and the
   * account has no password until its owner sets one (ADR-0051 point 9).
   */
  async register(email: string, password: string, username: string, firstName?: string, lastName?: string, code?: string): Promise<AuthResponseData> {
    const userId = localStorage.getItem(GUEST_ID_KEY);
    // Beta gate: BetaGate validates the magic-link token on mount and
    // stashes it in sessionStorage; we forward it to the backend here so
    // closed-beta enforcement doesn't require prop-drilling through the
    // entire onboarding component tree.
    let betaToken: string | undefined;
    try { betaToken = sessionStorage.getItem('showliner_beta_invite_token') ?? undefined; } catch {}

    // Audience (fan) signups are exempt from the closed-beta gate — BetaGate
    // sets this flag when the /register route is a fan signup. Forward it (and
    // consume it, one-shot) so the backend skips the beta gate for this account.
    let audience = false;
    try {
      if (sessionStorage.getItem('showliner_audience_signup') === '1') {
        audience = true;
        sessionStorage.removeItem('showliner_audience_signup');
      }
    } catch {}

    // Invited operator paths (claim / share-claim / team-invite) are exempt from
    // the closed-beta gate too — BetaGate stashes the magic-link token that got
    // them here. Unlike `audience`, this is NOT a bypass flag: the server
    // resolves the token against a real InviteToken row and only exempts when
    // its targetType matches the declared kind (services/betaGatePolicy.ts).
    let inviteContext: { kind: string; token: string } | undefined;
    try {
      const raw = sessionStorage.getItem('showliner_invite_entry');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.kind && parsed?.token) inviteContext = { kind: parsed.kind, token: parsed.token };
      }
      // Deliberately NOT one-shot: a failed register (e.g. "email already
      // registered") must be retryable without dead-ending on the gate. BetaGate
      // clears the key whenever /register is entered by a non-invited path.
    } catch {}

    const body = {
      email,
      ...(code ? { code } : { password }),
      username,
      firstName,
      lastName,
      userId,
      betaToken,
      audience,
      inviteContext,
    };

    try {
      const response: AxiosResponse<AuthResponseData> = await axios.post(`${this.authUrl}register`, body);

      // The JWT travels in the response body for backwards-compat with
      // anything still reading it, but the cookie set by the server is
      // the real auth. We only persist user data + flags here.
      if (response.data.user) {
        this.setUser(response.data.user);
        localStorage.setItem('hasRegistered', 'true');
        localStorage.setItem('isGuest', 'false');
        if (userId) {
          localStorage.removeItem(GUEST_ID_KEY);
        }
        // Beta token is single-use; clear it so a back-button trip won't
        // try to consume it again.
        try { sessionStorage.removeItem('showliner_beta_invite_token'); } catch {}
      }
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error('Registration error:', axiosError.response?.data || axiosError.message);
      throw axiosError;
    }
  }

  async login(email: string, password: string): Promise<LoginResult> {
    try {
      const response: AxiosResponse<AuthResponseData & {
        require2FA?: boolean;
        tempToken?: string;
        method?: TwoFactorMethod;
      }> = await axios.post(`${this.authUrl}login`, { email, password });

      // 2FA-enrolled: the server withheld the session. Hand the caller the
      // temp token so it can collect the second factor. Nothing is persisted
      // and no user is set — we are not signed in yet.
      if (response.data.require2FA && response.data.tempToken) {
        return {
          status: '2fa',
          tempToken: response.data.tempToken,
          method: response.data.method ?? 'totp',
        };
      }

      this.persistSignedInUser(response.data);
      return { status: 'ok', data: response.data };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error('Login error:', axiosError.response?.data || axiosError.message);
      throw axiosError;
    }
  }

  /**
   * Sign in with a passkey (ADR-0051). Opens the device's passkey sheet; the
   * browser throws `NotAllowedError` if the person closes it, which callers
   * treat as a cancel (`passkeyErrorMessage` returns null for it). A passkey
   * sign-in never stops at the second factor, so this only ever returns `ok`.
   * `autofill` waits on the email field's autofill list instead of opening the
   * sheet (see `getPasskey`).
   */
  async loginWithPasskey(
    opts: { autofill?: boolean; signal?: AbortSignal; onPicked?: () => void } = {},
  ): Promise<LoginResult> {
    const optionsRes = await axios.post(`${this.authUrl}passkeys/login/options`, {});
    const response = await getPasskey(optionsRes.data.options, opts);
    // The person chose a passkey; the session follows. A page uses this to
    // take charge of where they go next before the user context changes.
    opts.onPicked?.();
    const res: AxiosResponse<AuthResponseData> = await axios.post(`${this.authUrl}passkeys/login`, {
      challengeId: optionsRes.data.challengeId,
      response,
    });
    this.persistSignedInUser(res.data);
    return { status: 'ok', data: res.data };
  }

  /**
   * Ask the server to send the SMS login code. No-op server-side for TOTP
   * accounts, which have nothing to send.
   */
  async requestTwoFactorCode(tempToken: string): Promise<{ sent: boolean; phone?: string }> {
    const response = await axios.post(`${this.authUrl}2fa/challenge`, { tempToken });
    return { sent: !!response.data?.sent, phone: response.data?.phone };
  }

  /**
   * Complete login with the second factor. Accepts the method's own 6-digit
   * code or an 8-character backup code — the server tells them apart.
   */
  async verifyTwoFactorLogin(tempToken: string, code: string): Promise<AuthResponseData & {
    usedBackupCode?: boolean;
    backupCodesRemaining?: number;
  }> {
    try {
      const response = await axios.post(`${this.authUrl}2fa/login`, {
        tempToken,
        token: code.trim(),
      });
      this.persistSignedInUser(response.data);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error('2FA login error:', axiosError.response?.data || axiosError.message);
      throw axiosError;
    }
  }

  /**
   * Shared tail of every path that ends in a real session. The JWT itself
   * lives in the HttpOnly cookie the server set; this only caches the user
   * for presentation and flips the guest flags.
   */
  private persistSignedInUser(data: AuthResponseData): void {
    if (!data?.user) return;
    this.setUser(data.user);
    localStorage.setItem('hasRegistered', 'true');
    localStorage.setItem('isGuest', 'false');
  }

  /**
   * Hand off to Google/Apple.
   *
   * `redirect` is where the user should land once the round trip completes;
   * `OAuthCallback` reads it back out of `auth_redirect`.
   *
   * **Pass it here rather than writing `auth_redirect` before calling.** This
   * method used to open by setting `auth_redirect` to `window.location.pathname`,
   * which overwrote whatever the caller had stored one line earlier and dropped
   * the query string doing it. Every entry context that set a destination was
   * silently defeated: a guest conversion left for
   * `/register?convert=true&guestId=…` and came back to a bare `/register`,
   * which `classifyRegistrationEntry` reads as a plain signup, so the fan was
   * forwarded to the workspace builder and their room session was never
   * adopted. That clobber sat one line below an `oauthReturnTo()` written
   * specifically to prevent it, so the bug survived its own fix.
   *
   * The precedence rule now lives in `resolveOAuthRedirect`, named and tested,
   * rather than being implied by a side effect here.
   */
  loginWithProvider(provider: string, options: { redirect?: string } = {}): void {
    if (provider !== 'google' && provider !== 'apple') {
      console.error(`Provider ${provider} is not currently supported`);
      return;
    }

    localStorage.setItem('auth_redirect', resolveOAuthRedirect({
      requested: options.redirect,
      stored: localStorage.getItem('auth_redirect'),
      pathname: window.location.pathname,
      search: window.location.search,
    }));

    const returnOrigin = encodeURIComponent(window.location.origin);
    window.location.href = `${this.authUrl}${provider}?returnOrigin=${returnOrigin}`;
  }

  async createGuestUser(username: string, color: string): Promise<AuthResponseData> {
    try {
      const response: AxiosResponse<AuthResponseData> = await axios.post(`${this.authUrl}guest`, {
        username,
        color
      });

      if (response.data.user) {
        this.setUser(response.data.user);

        if (response.data.user.role === undefined) {
          const updatedUser = { ...response.data.user, role: 'guest' };
          this.setUser(updatedUser);
          response.data.user = updatedUser;
        }
      }
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error('Guest login error:', axiosError.response?.data || axiosError.message);
      throw axiosError;
    }
  }

  async convertGuestUser(
    email: string,
    password: string,
    username: string,
    session?: { roomCode?: string; guestIdentifier?: string }
  ): Promise<ConversionResponseData> {
    const currentUser = this.getUser();
    if (!currentUser || !currentUser.id) {
      throw new Error('You must be logged in as a guest user to convert to a registered account');
    }
    if (currentUser.role !== 'guest') {
      throw new Error('Only guest users can be converted to registered accounts');
    }
    const userId = currentUser.id;
    return this._performConversion(email, password, username, userId, session);
  }

  async convertGuestWithId(
    email: string,
    password: string,
    username: string,
    userId: string,
    session?: { roomCode?: string; guestIdentifier?: string }
  ): Promise<ConversionResponseData> {
    if (!userId) {
      throw new Error('User ID is required for conversion');
    }
    return this._performConversion(email, password, username, userId, session);
  }

  private async _performConversion(
    email: string,
    password: string,
    username: string,
    userId: string,
    // Phase D: ephemeral (Redis-only) guests have no Postgres row — the backend
    // materializes their balances/items from the room's Redis hash, so it needs
    // the room handle. roomCode being present is what selects that code path.
    session?: { roomCode?: string; guestIdentifier?: string }
  ): Promise<ConversionResponseData> {
    try {
      const response: AxiosResponse<ConversionResponseData> = await axios.post(`${this.authUrl}convert`, {
        userId,
        email,
        password,
        username,
        ...(session?.roomCode ? { roomCode: session.roomCode } : {}),
        ...(session?.guestIdentifier ? { guestIdentifier: session.guestIdentifier } : {}),
      });

      if (response.data.user) {
        this.setUser(response.data.user);
        localStorage.setItem('hasRegistered', 'true');
        localStorage.setItem('isGuest', 'false');
        localStorage.removeItem(GUEST_ID_KEY);
      }
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error('Conversion error:', axiosError.response?.data || axiosError.message);
      throw axiosError;
    }
  }

  /**
   * Adopt a guest session into the account you are ALREADY signed in as.
   *
   * The OAuth counterpart to {@link convertGuestWithId}: a guest who signs up
   * with Google or Apple has no password, so there is nothing for `/convert` to
   * check — the session proves who they are, and the server takes the target
   * account from it rather than from anything sent here.
   */
  async adoptGuestSession(session: {
    guestId: string;
    roomCode: string;
    guestIdentifier?: string;
  }): Promise<GuestConvertSummary | undefined> {
    // No auth header — the session cookie rides along via
    // `axios.defaults.withCredentials` (see apiClient setup).
    const response: AxiosResponse<{ success: boolean; claimed?: GuestConvertSummary }> =
      await axios.post(`${this.authUrl}adopt-guest-session`, session);
    return response.data.claimed;
  }

  async requestOTP(email: string, purpose?: 'signup'): Promise<{ success: boolean; expiresIn: number }> {
    try {
      const response = await axios.post(`${this.authUrl}otp/request`, purpose ? { email, purpose } : { email });
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error('OTP request error:', axiosError.response?.data || axiosError.message);
      throw axiosError;
    }
  }

  /**
   * Verify a passwordless email code.
   *
   * Like `login`, this can stop at a second factor: an account with 2FA on is
   * challenged here too, so the result is the same discriminated union. On
   * success `isNewUser` rides along on the `data`.
   */
  async verifyOTP(email: string, code: string): Promise<
    | { status: 'ok'; data: AuthResponseData & { isNewUser?: boolean } }
    | { status: '2fa'; tempToken: string; method: TwoFactorMethod }
  > {
    try {
      const response: AxiosResponse<AuthResponseData & {
        isNewUser?: boolean;
        require2FA?: boolean;
        tempToken?: string;
        method?: TwoFactorMethod;
      }> = await axios.post(`${this.authUrl}otp/verify`, { email, code });

      if (response.data.require2FA && response.data.tempToken) {
        return {
          status: '2fa',
          tempToken: response.data.tempToken,
          method: response.data.method ?? 'totp',
        };
      }

      this.persistSignedInUser(response.data);
      return { status: 'ok', data: response.data };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error('OTP verify error:', axiosError.response?.data || axiosError.message);
      throw axiosError;
    }
  }

  /**
   * Email verification for the signed-in user (distinct from the passwordless
   * OTP login above). The code is always sent to the account's own email.
   */
  async requestEmailVerification(): Promise<{ success: boolean; alreadyVerified?: boolean; expiresIn?: number }> {
    const response = await axios.post(`${this.authUrl}email/verify/request`, {});
    return response.data;
  }

  /** Confirm the emailed code and mark the signed-in user's email verified. */
  async verifyEmail(code: string): Promise<{ success: boolean; alreadyVerified?: boolean; emailVerifiedAt?: string }> {
    const response = await axios.post(`${this.authUrl}email/verify`, { code });
    return response.data;
  }

  /**
   * Request a password-reset email. Backend always returns 200 to avoid
   * leaking whether an account exists for the address — the UI should
   * show the same "check your email" confirmation either way.
   */
  async requestPasswordReset(email: string): Promise<{ message: string }> {
    try {
      const response = await axios.post(`${this.authUrl}forgot-password`, { email });
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error('Password reset request error:', axiosError.response?.data || axiosError.message);
      throw axiosError;
    }
  }

  /**
   * Consume a reset token + set a new password. Throws on invalid/expired
   * token (400). The endpoint enforces 8-char minimum server-side; UI
   * should mirror that to give immediate feedback.
   */
  async resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
    try {
      const response = await axios.post(`${this.authUrl}reset-password`, { token, newPassword });
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error('Password reset error:', axiosError.response?.data || axiosError.message);
      throw axiosError;
    }
  }

  async logout(): Promise<boolean> {
    try {
      // Cookie travels with the request automatically
      // (axios.defaults.withCredentials, set in apiClient setup). The
      // backend clears both cookies in its response.
      await axios.post(`${this.authUrl}logout`, {});
      this.clearAuthData();
      return true;
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error('Logout error:', axiosError.response?.data || axiosError.message);
      // Best-effort local cleanup even if the network call failed.
      this.clearAuthData();
      return false;
    }
  }

  /**
   * Drop the locally-cached session without telling the server.
   *
   * For the case where the server has ALREADY told us: a 401 means the cookie
   * is gone, so `logout()` — which posts to the API — has nothing to revoke
   * and would only add a second failing request. The cache still says signed
   * in though (the session cookie is HttpOnly, so `isLoggedIn()` reads
   * localStorage), and until it is cleared `ProtectedRoute` keeps waving the
   * user through to a console that cannot load anything.
   *
   * Fires the `onUserChanged` subscription, so `UserContext` — and every
   * `isAuthenticated` derived from it — catches up in the same tick.
   */
  clearLocalSession(): void {
    this.clearAuthData();
  }

  private clearAuthData(): void {
    // Wipe the legacy `auth_token` key so older browsers upgrading
    // from PR1 don't retain stale Bearer material — the cookie is the
    // only auth path now.
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem('isGuest');
    localStorage.removeItem('hasRegistered');
    this.notifyUserChanged();
  }

  // `refreshToken()` was REMOVED along with the `POST /api/auth/refresh-token`
  // endpoint it called (security — see the note in routes/authRoutes.ts). It had
  // no callers, and it posted an empty body to an endpoint that required a token
  // in the body, so it could only ever have returned 400. Sliding renewal is
  // server-side now.

  async getCurrentUser(): Promise<AuthUser> {
    try {
      // Cookie auto-attached. Backend returns 401 if no valid session,
      // which UserContext handles by clearing the user cache.
      const response: AxiosResponse<AuthUser> = await axios.get(`${this.usersUrl}me`);
      this.setUser(response.data);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      // 401 is the expected "no session" signal on public pages —
      // don't pollute the console with it. Other failures are still
      // worth logging.
      if (axiosError.response?.status === 401) {
        this.clearAuthData();
      } else {
        console.error('Get user error:', axiosError.response?.data || axiosError.message);
      }
      throw axiosError;
    }
  }

  getUser(): AuthUser | null {
    const userJson = localStorage.getItem(USER_KEY);
    try {
      return userJson ? JSON.parse(userJson) as AuthUser : null;
    } catch (e) {
      console.error("Error parsing user JSON from localStorage", e);
      return null;
    }
  }

  setUser(user: AuthUser): void {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    this.notifyUserChanged();
  }

  /**
   * Best-effort check for whether the user is logged in. Reads the
   * cached user record (the cookie itself is HttpOnly and unreadable
   * from JS). For an authoritative answer, call getCurrentUser().
   */
  isLoggedIn(): boolean {
    return !!this.getUser();
  }

  hasRole(roles: string | string[]): boolean {
    const user = this.getUser();
    if (!user || !user.role) return false;

    if (Array.isArray(roles)) {
      return roles.includes(user.role);
    }
    return user.role === roles;
  }

  /**
   * @deprecated Returns an empty object. Auth flows via the HttpOnly
   * `showliner_session` cookie, which axios attaches automatically
   * when `withCredentials` is set (see apiClient.setupAxiosClient).
   * 36+ existing callsites still pass `headers: getAuthHeader()` to
   * axios; rather than touch every one of them in this migration, we
   * keep this stub so they compile and ship empty headers (harmless;
   * cookie does the work). A future cleanup pass should drop the
   * pattern entirely and remove this method. Do NOT add new callers.
   */
  getAuthHeader(): Record<string, never> {
    return {};
  }

}

const authService = new AuthService();
export default authService;
